import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'
import type * as rxjs from 'rxjs'

export type FileRequest = Pick<
  ents.core.common.fileManagement.Key.Parts.Input,
  'context' | 'qualifier' | 'purpose'
>
export namespace FileRequest {
  /** File extensions supported by the shared upload flow. */
  export const supportedUploadExtensions = [
    'xlsx',
    'xls',
    'csv',
    'pdf',
    'doc',
    'docx',
    'ppt',
    'pptx',
    'txt',
  ] as const

  export const isFileRequest = TypeChecker.Factory.pick(
    ents.core.common.fileManagement.Key.Parts.Input.isInput,
    ['context', 'qualifier', 'purpose'],
  )
  export type ForDownload = FileRequest &
    Required<Pick<ents.core.common.fileManagement.Key.Parts, 'id'>> &
    Pick<ents.core.common.fileManagement.Key.Parts, 'extension'>

  export type ForUpload = FileRequest & {
    file: File
    /**
     * Optional function to resolve the name of the {@link ForUpload.file} being
     * uploaded
     */
    nameResolver?: (
      /** Actual file being uploaded */
      file: File,
    ) => string | rxjs.Observable<string>
  }
  export namespace ForUpload {
    export const isForUpload = TypeChecker.Factory.intersection(
      isFileRequest,
      TypeChecker.Factory.object<Pick<ForUpload, 'file'>>({
        file: TypeChecker.Factory.instance(File),
      }),
    )
  }

  /**
   *
   */
  export function getKey(
    request: FileRequest.ForDownload | FileRequest.ForUpload,
  ): ents.core.common.fileManagement.Key {
    if (FileRequest.ForUpload.isForUpload(request)) {
      const { purpose, context, file, qualifier } = request
      const { name: id, extension } = getFileInfo(file)

      return ents.core.common.fileManagement.Key.from({
        context,
        extension,
        id,
        purpose,
        qualifier,
      })
    } else {
      const { id, purpose, context, extension, qualifier } = request
      return ents.core.common.fileManagement.Key.from({
        context,
        extension,
        id,
        purpose,
        qualifier,
      })
    }
  }

  /**
   * Extracts file name and extension from various input types
   */
  export function getFileInfo(input: string | File | { fileName: string }): {
    name: string
    extension: string
  } {
    // Extract the fileName string from different input types
    let fileName: string
    if (typeof input === 'string') {
      fileName = input
    } else if (input instanceof File) {
      fileName = input.name
    } else {
      fileName = input.fileName
    }

    const lastDotIndex = fileName.lastIndexOf('.')
    if (lastDotIndex !== -1) {
      return {
        extension: fileName.substring(lastDotIndex + 1),
        name: fileName.substring(0, lastDotIndex),
      }
    }
    throw new Error(`File name must include an extension: "${fileName}"`)
  }

  /** Checks whether a file can enter the shared upload flow. */
  export function isSupportedUploadFile(
    input: string | File | { fileName: string },
  ): boolean {
    try {
      const { extension } = getFileInfo(input)
      return supportedUploadExtensions.some(
        supportedExtension =>
          supportedExtension === extension.toLocaleLowerCase(),
      )
    } catch {
      return false
    }
  }
}
